import { useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  Mail, Phone, MapPin, Car, Calendar, Pencil, AlertCircle, RefreshCw,
  Plus, MessageCircle, Activity, History, Shield, Download, Trash2,
  ExternalLink, ChevronLeft, MoreHorizontal,
} from 'lucide-react';
import {
  Button, Tabs, TabsList, TabsTrigger, TabsContent, Badge, UserAvatar,
  EmptyState, Skeleton, ConfirmDialog, toast, DropdownMenu, DropdownMenuTrigger,
  DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator,
  DetailSection, PropertyRow, StatusBadge,
} from '../../components/ui';
import { Modal } from '../../components/ui/modal';
import { useCustomer, useCustomerActivity } from '../../hooks/useCustomers';
import { VehicleForm } from '../../components/forms/VehicleForm';
import { CustomerForm } from '../../components/forms/CustomerForm';
import { CustomerMetricsSummary, CustomerActivityTimeline } from '../../components/customers';
import { EntityAuditLog } from '../../components/audit';
import { customersApi } from '../../services/customersApi';
import { detailStyles, pageStyles, listStyles } from '../../lib/styles';
import type { UnifiedCaseType } from '@leal-crm/shared';

/**
 * Map booking type to unified case type for workflow lookup
 */
function getBookingType(type?: string): UnifiedCaseType {
  switch (type) {
    case 'seguro':
      return 'insurance';
    case 'garantia':
      return 'warranty';
    default:
      return 'private';
  }
}

export function CustomerDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [isVehicleModalOpen, setIsVehicleModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isGdprDeleteOpen, setIsGdprDeleteOpen] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  const { data: customer, isLoading, isError, error, refetch } = useCustomer(id);
  const { data: activity, isLoading: isActivityLoading } = useCustomerActivity(id);

  const handleExportData = async () => {
    if (!id) return;
    setIsExporting(true);
    try {
      const data = await customersApi.exportGdprData(id);
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `customer-data-${id}-${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast({ title: 'Datos exportados' });
    } catch (err) {
      toast({ title: 'Error al exportar', variant: 'destructive' });
    } finally {
      setIsExporting(false);
    }
  };

  const handleGdprDelete = async () => {
    if (!id) return;
    try {
      await customersApi.gdprDelete(id);
      toast({ title: 'Datos eliminados' });
      navigate('/customers');
    } catch (err) {
      toast({ title: 'Error al eliminar', variant: 'destructive' });
      throw err;
    }
  };

  if (isLoading) return <CustomerDetailSkeleton />;

  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-8">
        <AlertCircle className="h-8 w-8 text-red-500 mb-3" />
        <p className="text-sm text-gray-900 mb-1">Error al cargar cliente</p>
        <p className="text-xs text-gray-500 mb-3">{(error as Error)?.message}</p>
        <div className="flex gap-2">
          <Button variant="secondary" size="sm" onClick={() => refetch()}>
            <RefreshCw className="h-3 w-3 mr-1.5" /> Reintentar
          </Button>
          <Link to="/customers"><Button variant="secondary" size="sm">Volver</Button></Link>
        </div>
      </div>
    );
  }

  if (!customer) {
    return (
      <div className="text-center py-8">
        <p className="text-xs text-gray-500">Cliente no encontrado</p>
        <Link to="/customers" className="text-xs text-blue-600 hover:underline mt-2 block">Volver</Link>
      </div>
    );
  }

  const createdDate = new Date(customer.createdAt * 1000);
  const vehicles = customer.vehicles || [];

  return (
    <div className={detailStyles.container}>
      {/* Compact Header */}
      <div className={pageStyles.header}>
        <div className={pageStyles.headerLeft}>
          <Link to="/customers" className={pageStyles.backButton}>
            <ChevronLeft className="h-4 w-4" />
          </Link>
          <UserAvatar name={customer.name} size="sm" />
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <h1 className={pageStyles.title}>{customer.name}</h1>
              <Badge variant="default" className="text-[10px] px-1.5 py-0">Activo</Badge>
            </div>
            <p className={pageStyles.subtitle}>
              Cliente desde {createdDate.toLocaleDateString('es-ES', { month: 'short', year: 'numeric' })}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-1.5">
          <Button variant="secondary" size="sm" onClick={() => setIsEditModalOpen(true)}>
            <Pencil className="h-3 w-3 mr-1" /> Editar
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="secondary" size="sm" className="px-2">
                <MoreHorizontal className="h-3.5 w-3.5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="text-xs">
              <DropdownMenuItem onClick={handleExportData} disabled={isExporting} className="text-xs">
                <Download className="mr-2 h-3.5 w-3.5" />
                {isExporting ? 'Exportando...' : 'Exportar (RGPD)'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => setIsGdprDeleteOpen(true)} className="text-xs text-destructive">
                <Trash2 className="mr-2 h-3.5 w-3.5" /> Eliminar (RGPD)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Contact Info */}
      <DetailSection title="Contacto">
        <PropertyRow label="Email" icon={<Mail className="h-3 w-3" />}>
          <a href={`mailto:${customer.email}`} className="text-blue-600 hover:underline truncate">
            {customer.email}
          </a>
        </PropertyRow>
        <PropertyRow label="Teléfono" icon={<Phone className="h-3 w-3" />}>
          <div className="flex items-center gap-2">
            <a href={`tel:${customer.phone}`} className="text-blue-600 hover:underline">
              {customer.phone}
            </a>
            <a
              href={`https://wa.me/${customer.phone?.replace(/\D/g, '')}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex h-5 w-5 items-center justify-center rounded-full bg-green-500 text-white hover:bg-green-600"
              title="WhatsApp"
            >
              <MessageCircle className="h-3 w-3" />
            </a>
          </div>
        </PropertyRow>
        <PropertyRow label="Dirección" icon={<MapPin className="h-3 w-3" />}>
          {customer.address || '—'}
        </PropertyRow>
        {(customer as { dni?: string }).dni && (
          <PropertyRow label="DNI/NIE" icon={<Shield className="h-3 w-3" />}>
            {(customer as { dni?: string }).dni}
          </PropertyRow>
        )}
      </DetailSection>

      {/* Tabs */}
      <Tabs defaultValue="vehicles" className="mt-3">
        <TabsList className="h-8">
          <TabsTrigger value="vehicles" className="text-xs gap-1.5 px-3 py-1">
            <Car className="h-3 w-3" /> Vehículos ({vehicles.length})
          </TabsTrigger>
          <TabsTrigger value="bookings" className="text-xs gap-1.5 px-3 py-1">
            <Calendar className="h-3 w-3" /> Citas
          </TabsTrigger>
          <TabsTrigger value="activity" className="text-xs gap-1.5 px-3 py-1">
            <Activity className="h-3 w-3" /> Actividad
          </TabsTrigger>
          <TabsTrigger value="audit" className="text-xs gap-1.5 px-3 py-1">
            <History className="h-3 w-3" /> Historial
          </TabsTrigger>
        </TabsList>

        <TabsContent value="vehicles" className="mt-3">
          {vehicles.length === 0 ? (
            <EmptyState
              icon={Car}
              title="Sin vehículos"
              description="Este cliente no tiene vehículos registrados"
              actionLabel="Agregar vehículo"
              onAction={() => setIsVehicleModalOpen(true)}
              compact
            />
          ) : (
            <div className="space-y-2">
              <div className="flex justify-end">
                <Button size="sm" onClick={() => setIsVehicleModalOpen(true)}>
                  <Plus className="h-3 w-3 mr-1" /> Agregar
                </Button>
              </div>
              <div className={detailStyles.section}>
                <div className={listStyles.container}>
                  {vehicles.map((vehicle) => (
                    <div key={vehicle.id} className={listStyles.item}>
                      <div className="flex items-center gap-2.5 min-w-0">
                        <div className="w-7 h-7 rounded bg-gray-100 flex items-center justify-center shrink-0">
                          <Car className="h-3.5 w-3.5 text-gray-500" />
                        </div>
                        <div className="min-w-0">
                          <p className={listStyles.itemPrimary}>{vehicle.make} {vehicle.model}</p>
                          <p className={listStyles.itemSecondary}>
                            {vehicle.registration}{vehicle.year && ` · ${vehicle.year}`}{vehicle.color && ` · ${vehicle.color}`}
                          </p>
                        </div>
                      </div>
                      <Link to={`/vehicles/${vehicle.id}`}>
                        <Button variant="ghost" size="sm" className="text-xs h-7 px-2">Ver</Button>
                      </Link>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </TabsContent>

        <TabsContent value="bookings" className="mt-3">
          {isActivityLoading ? (
            <div className="space-y-2">
              {[1, 2, 3].map((i) => <Skeleton key={i} className="h-12 w-full rounded" />)}
            </div>
          ) : (() => {
            const bookingActivities = (activity?.recentActivity || []).filter((a) => a.type === 'booking');
            if (bookingActivities.length === 0) {
              return (
                <EmptyState
                  icon={Calendar}
                  title="Sin citas"
                  description="Este cliente no tiene citas registradas"
                  actionLabel="Nueva cita"
                  actionHref="/jobs"
                  compact
                />
              );
            }
            return (
              <div className={detailStyles.section}>
                <div className={listStyles.container}>
                  {bookingActivities.map((booking) => {
                    const date = new Date(booking.date);
                    return (
                      <div key={booking.id} className={listStyles.item}>
                        <div className="flex items-center gap-2.5 min-w-0">
                          <div className="w-7 h-7 rounded bg-blue-50 flex items-center justify-center shrink-0">
                            <Calendar className="h-3.5 w-3.5 text-blue-500" />
                          </div>
                          <div className="min-w-0">
                            <p className={listStyles.itemPrimary}>{booking.title}</p>
                            <p className={listStyles.itemSecondary}>
                              {date.toLocaleDateString('es-ES', { day: 'numeric', month: 'short', year: 'numeric' })}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          {booking.status && <StatusBadge status={booking.status} bookingType={getBookingType((booking as { caseCategory?: string }).caseCategory)} />}
                          <Link to={`/bookings/${booking.id}`}>
                            <Button variant="ghost" size="sm" className="h-7 w-7 p-0">
                              <ExternalLink className="h-3 w-3" />
                            </Button>
                          </Link>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })()}
        </TabsContent>

        <TabsContent value="activity" className="mt-3 space-y-3">
          {activity?.metrics && (
            <CustomerMetricsSummary metrics={activity.metrics} isLoading={isActivityLoading} />
          )}
          <CustomerActivityTimeline activities={activity?.recentActivity || []} isLoading={isActivityLoading} />
        </TabsContent>

        <TabsContent value="audit" className="mt-3">
          <EntityAuditLog entityType="customer" entityId={id!} title="Historial de cambios" />
        </TabsContent>
      </Tabs>

      {/* Modals */}
      <Modal isOpen={isVehicleModalOpen} onClose={() => setIsVehicleModalOpen(false)} title="Agregar vehículo" size="md">
        <VehicleForm
          customerId={id!}
          onSuccess={() => { setIsVehicleModalOpen(false); refetch(); }}
          onCancel={() => setIsVehicleModalOpen(false)}
        />
      </Modal>

      <ConfirmDialog
        open={isGdprDeleteOpen}
        onOpenChange={setIsGdprDeleteOpen}
        title="Eliminar datos (RGPD)"
        description={`Eliminará permanentemente todos los datos de ${customer?.name}. Esta acción es irreversible.`}
        confirmText="Eliminar"
        cancelText="Cancelar"
        variant="destructive"
        onConfirm={handleGdprDelete}
      />

      <Modal isOpen={isEditModalOpen} onClose={() => setIsEditModalOpen(false)} title="Editar cliente" size="md">
        <CustomerForm
          customerId={id}
          initialData={customer ? {
            name: customer.name,
            email: customer.email,
            phone: customer.phone,
            address: customer.address || '',
            dni: (customer as { dni?: string }).dni || '',
          } : undefined}
          onSuccess={() => { setIsEditModalOpen(false); refetch(); }}
          onCancel={() => setIsEditModalOpen(false)}
        />
      </Modal>
    </div>
  );
}

function CustomerDetailSkeleton() {
  return (
    <div className="space-y-3">
      <div className="flex items-center gap-3">
        <Skeleton className="h-8 w-8 rounded-full" />
        <div className="space-y-1.5">
          <Skeleton className="h-4 w-32" />
          <Skeleton className="h-3 w-24" />
        </div>
      </div>
      <Skeleton className="h-32 w-full rounded-lg" />
      <Skeleton className="h-48 w-full rounded-lg" />
    </div>
  );
}
